/**
 * Функции для конвертации различных единиц измерения
 */

// Определения единиц измерения
const unitDefinitions = {
  // Длина
  length: {
    m: 1, // метр (базовая единица)
    km: 1000, // километр
    cm: 0.01, // сантиметр
    mm: 0.001, // миллиметр
    in: 0.0254, // дюйм
    ft: 0.3048, // фут
    yd: 0.9144, // ярд
    mi: 1609.344 // миля
  },
  
  // Масса/вес
  mass: {
    kg: 1, // килограмм (базовая единица)
    g: 0.001, // грамм
    mg: 0.000001, // миллиграмм
    t: 1000, // тонна
    lb: 0.45359237, // фунт
    oz: 0.028349523125, // унция
    st: 6.35029318, // стоун
  },
  
  // Площадь
  area: {
    m2: 1, // квадратный метр (базовая единица)
    km2: 1000000, // квадратный километр
    ha: 10000, // гектар
    a: 100, // ар (сотка)
    cm2: 0.0001, // квадратный сантиметр
    mm2: 0.000001, // квадратный миллиметр
    in2: 0.00064516, // квадратный дюйм
    ft2: 0.09290304, // квадратный фут
    yd2: 0.83612736, // квадратный ярд
    ac: 4046.8564224 // акр
  },
  
  // Объем
  volume: {
    m3: 1, // кубический метр (базовая единица)
    l: 0.001, // литр
    ml: 0.000001, // миллилитр
    cm3: 0.000001, // кубический сантиметр
    mm3: 0.000000001, // кубический миллиметр
    in3: 0.000016387064, // кубический дюйм
    ft3: 0.028316846592, // кубический фут
    gal_us: 0.003785411784, // галлон (США)
    gal_uk: 0.00454609, // галлон (Великобритания)
    qt_us: 0.000946352946, // кварта (США)
    pt_us: 0.000473176473 // пинта (США)
  },
  
  // Температура
  // Примечание: для температуры требуется специальная обработка, а не простое умножение
  temperature: {
    c: 'celsius', // Цельсий (базовая единица)
    f: 'fahrenheit', // Фаренгейт
    k: 'kelvin' // Кельвин
  },
  
  // Время
  time: {
    s: 1, // секунда (базовая единица)
    ms: 0.001, // миллисекунда
    min: 60, // минута
    h: 3600, // час
    d: 86400, // день
    wk: 604800, // неделя
    mo: 2629746, // месяц (среднее)
    yr: 31556952 // год (среднее)
  },
  
  // Скорость
  speed: {
    m_s: 1, // метр в секунду (базовая единица)
    km_h: 0.277777778, // километр в час
    mph: 0.44704, // миля в час
    kn: 0.514444444, // узел
    ft_s: 0.3048 // фут в секунду
  },
  
  // Давление
  pressure: {
    pa: 1, // паскаль (базовая единица)
    kpa: 1000, // килопаскаль
    mpa: 1000000, // мегапаскаль
    bar: 100000, // бар
    atm: 101325, // атмосфера
    mmhg: 133.322, // миллиметр ртутного столба
    psi: 6894.75729 // фунт на квадратный дюйм
  },
  
  // Энергия
  energy: {
    j: 1, // джоуль (базовая единица)
    kj: 1000, // килоджоуль
    cal: 4.184, // калория
    kcal: 4184, // килокалория
    wh: 3600, // ватт-час
    kwh: 3600000, // киловатт-час
    ev: 1.602176634e-19, // электронвольт
    btu: 1055.05585262 // британская тепловая единица
  }
};

/**
 * Конвертация единиц измерения
 * 
 * @param {number} value - Исходное значение
 * @param {string} fromUnit - Исходная единица измерения
 * @param {string} toUnit - Целевая единица измерения
 * @param {string} type - Тип измерения (length, mass, area, volume, temperature, time, speed, pressure, energy)
 * @returns {number} Сконвертированное значение
 */
export const convertUnits = (value, fromUnit, toUnit, type) => {
  // Проверка поддерживаемых типов
  if (!unitDefinitions[type]) {
    throw new Error(`Unsupported measurement type: ${type}`);
  }
  
  // Проверка поддерживаемых единиц
  if (!unitDefinitions[type][fromUnit]) {
    throw new Error(`Unsupported unit for ${type}: ${fromUnit}`);
  }
  
  if (!unitDefinitions[type][toUnit]) {
    throw new Error(`Unsupported unit for ${type}: ${toUnit}`);
  }
  
  // Специальная обработка для температуры
  if (type === 'temperature') {
    return convertTemperature(value, fromUnit, toUnit);
  }
  
  // Для других типов используем коэффициенты конвертации
  const fromFactor = unitDefinitions[type][fromUnit];
  const toFactor = unitDefinitions[type][toUnit];
  
  // Сначала конвертируем в базовую единицу, затем в целевую
  return (value * fromFactor) / toFactor;
};

/**
 * Конвертация температуры между шкалами Цельсия, Фаренгейта и Кельвина
 * 
 * @param {number} value - Исходное значение
 * @param {string} fromUnit - Исходная единица (c, f, k)
 * @param {string} toUnit - Целевая единица (c, f, k)
 * @returns {number} Сконвертированное значение
 */
export const convertTemperature = (value, fromUnit, toUnit) => {
  // Если единицы одинаковые, возвращаем исходное значение
  if (fromUnit === toUnit) {
    return value;
  }
  
  // Сначала конвертируем в Цельсии (наша "базовая" единица)
  let celsius;
  
  switch (fromUnit) {
    case 'c':
      celsius = value;
      break;
    case 'f':
      celsius = (value - 32) * 5/9;
      break;
    case 'k':
      celsius = value - 273.15;
      break;
    default:
      throw new Error(`Unsupported temperature unit: ${fromUnit}`);
  }
  
  // Затем конвертируем из Цельсиев в целевую единицу
  switch (toUnit) {
    case 'c':
      return celsius;
    case 'f':
      return celsius * 9/5 + 32;
    case 'k':
      return celsius + 273.15;
    default:
      throw new Error(`Unsupported temperature unit: ${toUnit}`);
  }
};

/**
 * Конвертация валют
 * 
 * @param {number} amount - Исходная сумма
 * @param {string} fromCurrency - Исходная валюта
 * @param {string} toCurrency - Целевая валюта
 * @param {Object} rates - Объект с курсами валют (относительно базовой валюты)
 * @param {string} baseCurrency - Базовая валюта для курсов
 * @returns {number} Сконвертированная сумма
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, rates, baseCurrency = 'USD') => {
  // Если валюты одинаковые, возвращаем исходную сумму
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  // Проверка доступности курсов
  if (!rates[fromCurrency] && fromCurrency !== baseCurrency) {
    throw new Error(`Exchange rate not available for ${fromCurrency}`);
  }
  
  if (!rates[toCurrency] && toCurrency !== baseCurrency) {
    throw new Error(`Exchange rate not available for ${toCurrency}`);
  }
  
  // Конвертация через базовую валюту
  let inBaseCurrency;
  
  if (fromCurrency === baseCurrency) {
    inBaseCurrency = amount;
  } else {
    inBaseCurrency = amount / rates[fromCurrency];
  }
  
  if (toCurrency === baseCurrency) {
    return inBaseCurrency;
  } else {
    return inBaseCurrency * rates[toCurrency];
  }
};

/**
 * Получение всех доступных единиц измерения для указанного типа
 * 
 * @param {string} type - Тип измерения
 * @returns {Array} Массив доступных единиц
 */
export const getAvailableUnits = (type) => {
  if (!unitDefinitions[type]) {
    throw new Error(`Unsupported measurement type: ${type}`);
  }
  
  return Object.keys(unitDefinitions[type]);
};

/**
 * Получение полных названий для сокращений единиц измерения
 * 
 * @param {string} unit - Сокращение единицы измерения
 * @param {string} type - Тип измерения
 * @returns {Object} Объект с полным названием и символом
 */
export const getUnitDetails = (unit, type) => {
  const unitNames = {
    // Длина
    m: { name: 'метр', symbol: 'м' },
    km: { name: 'километр', symbol: 'км' },
    cm: { name: 'сантиметр', symbol: 'см' },
    mm: { name: 'миллиметр', symbol: 'мм' },
    in: { name: 'дюйм', symbol: 'дюйм' },
    ft: { name: 'фут', symbol: 'фт' },
    yd: { name: 'ярд', symbol: 'ярд' },
    mi: { name: 'миля', symbol: 'миль' },
    
    // Масса/вес
    kg: { name: 'килограмм', symbol: 'кг' },
    g: { name: 'грамм', symbol: 'г' },
    mg: { name: 'миллиграмм', symbol: 'мг' },
    t: { name: 'тонна', symbol: 'т' },
    lb: { name: 'фунт', symbol: 'фунт' },
    oz: { name: 'унция', symbol: 'унц' },
    st: { name: 'стоун', symbol: 'стоун' },
    
    // Площадь
    m2: { name: 'квадратный метр', symbol: 'м²' },
    km2: { name: 'квадратный километр', symbol: 'км²' },
    ha: { name: 'гектар', symbol: 'га' },
    a: { name: 'ар (сотка)', symbol: 'а' },
    cm2: { name: 'квадратный сантиметр', symbol: 'см²' },
    mm2: { name: 'квадратный миллиметр', symbol: 'мм²' },
    in2: { name: 'квадратный дюйм', symbol: 'дюйм²' },
    ft2: { name: 'квадратный фут', symbol: 'фт²' },
    yd2: { name: 'квадратный ярд', symbol: 'ярд²' },
    ac: { name: 'акр', symbol: 'акр' },
    
    // Объем
    m3: { name: 'кубический метр', symbol: 'м³' },
    l: { name: 'литр', symbol: 'л' },
    ml: { name: 'миллилитр', symbol: 'мл' },
    cm3: { name: 'кубический сантиметр', symbol: 'см³' },
    mm3: { name: 'кубический миллиметр', symbol: 'мм³' },
    in3: { name: 'кубический дюйм', symbol: 'дюйм³' },
    ft3: { name: 'кубический фут', symbol: 'фт³' },
    gal_us: { name: 'галлон (США)', symbol: 'гал (США)' },
    gal_uk: { name: 'галлон (Великобритания)', symbol: 'гал (Брит)' },
    qt_us: { name: 'кварта (США)', symbol: 'кв (США)' },
    pt_us: { name: 'пинта (США)', symbol: 'пт (США)' },
    
    // Температура
    c: { name: 'градус Цельсия', symbol: '°C' },
    f: { name: 'градус Фаренгейта', symbol: '°F' },
    k: { name: 'кельвин', symbol: 'K' },
    
    // Время
    s: { name: 'секунда', symbol: 'с' },
    ms: { name: 'миллисекунда', symbol: 'мс' },
    min: { name: 'минута', symbol: 'мин' },
    h: { name: 'час', symbol: 'ч' },
    d: { name: 'день', symbol: 'д' },
    wk: { name: 'неделя', symbol: 'нед' },
    mo: { name: 'месяц', symbol: 'мес' },
    yr: { name: 'год', symbol: 'г' },
    
    // Скорость
    m_s: { name: 'метр в секунду', symbol: 'м/с' },
    km_h: { name: 'километр в час', symbol: 'км/ч' },
    mph: { name: 'миля в час', symbol: 'миль/ч' },
    kn: { name: 'узел', symbol: 'уз' },
    ft_s: { name: 'фут в секунду', symbol: 'фт/с' },
    
    // Давление
    pa: { name: 'паскаль', symbol: 'Па' },
    kpa: { name: 'килопаскаль', symbol: 'кПа' },
    mpa: { name: 'мегапаскаль', symbol: 'МПа' },
    bar: { name: 'бар', symbol: 'бар' },
    atm: { name: 'атмосфера', symbol: 'атм' },
    mmhg: { name: 'миллиметр ртутного столба', symbol: 'мм рт.ст.' },
    psi: { name: 'фунт на квадратный дюйм', symbol: 'psi' },
    
    // Энергия
    j: { name: 'джоуль', symbol: 'Дж' },
    kj: { name: 'килоджоуль', symbol: 'кДж' },
    cal: { name: 'калория', symbol: 'кал' },
    kcal: { name: 'килокалория', symbol: 'ккал' },
    wh: { name: 'ватт-час', symbol: 'Вт⋅ч' },
    kwh: { name: 'киловатт-час', symbol: 'кВт⋅ч' },
    ev: { name: 'электронвольт', symbol: 'эВ' },
    btu: { name: 'британская тепловая единица', symbol: 'БТЕ' }
  };
  
  // Проверка существования единицы измерения
  if (!unitDefinitions[type] || !unitDefinitions[type][unit]) {
    throw new Error(`Unsupported unit: ${unit} for type: ${type}`);
  }
  
  // Возвращаем детали единицы измерения
  return unitNames[unit] || { name: unit, symbol: unit };
};

/**
 * Конвертация временных зон
 * 
 * @param {Date} date - Исходная дата
 * @param {string} fromTimezone - Исходная временная зона
 * @param {string} toTimezone - Целевая временная зона
 * @returns {Date} Дата в новой временной зоне
 */
export const convertTimezone = (date, fromTimezone, toTimezone) => {
  // Используем Intl.DateTimeFormat для получения смещения временной зоны
  const getTimezoneOffset = (timezone, date) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const map = {};
    parts.forEach(part => {
      map[part.type] = part.value;
    });
    
    // Преобразуем в UTC
    const year = parseInt(map.year, 10);
    const month = parseInt(map.month, 10) - 1; // месяцы в JS с 0
    const day = parseInt(map.day, 10);
    const hour = parseInt(map.hour, 10);
    const minute = parseInt(map.minute, 10);
    const second = parseInt(map.second, 10);
    
    return Date.UTC(year, month, day, hour, minute, second);
  };
  
  // Получаем время в обеих временных зонах
  const fromTime = getTimezoneOffset(fromTimezone, date);
  const toTime = getTimezoneOffset(toTimezone, date);
  
  // Разница в миллисекундах
  const offset = toTime - fromTime;
  
  // Применяем смещение к исходной дате
  const result = new Date(date.getTime() - offset);
  
  return result;
}